import {
  CAST_DUCK_ATTACK_SEC,
  CAST_DUCK_RELEASE_SEC,
  CAST_MUSIC_DUCK_GAIN,
  DUEL_ACTING_GAIN,
  MENU_MUSIC_GAIN,
  VOICE_DUCK_ATTACK_SEC,
  VOICE_DUCK_RELEASE_SEC,
  VOICE_MUSIC_DUCK_GAIN,
  activityDuck,
  activityWhileDueling,
  voiceDucksMusic,
  type DuelActivity,
} from './levels';

/**
 * Two music loops share one bus, under the chimes and the voice.
 * Menus keep the lobby loop at unity. A duel crossfades to Welcome to
 * Thistlewood. The bed is 4 dB down, then the 12 dB activity duck stays
 * down for the whole duel and opens again only when the duel ends.
 * Voice lines do not dip that bed further. Trick casts still duck on
 * their own node. Victory and defeat duck the last node to silence.
 * The power stinger does not.
 */

export const LOBBY_LOOP_SAMPLES = 8_064_000;
export const LOBBY_RATE = 44_100;
/** 8,064,000 / 44,100. The published length is 182.857s. */
export const LOBBY_LOOP_SECONDS = LOBBY_LOOP_SAMPLES / LOBBY_RATE;
export const LOBBY_FADE_SEC = 0.6;
/** How fast victory and defeat fade the music out. The power stinger does not use this. */
export const STINGER_MUSIC_FADE = 0.3;

/**
 * Duel acting level, about -4 dB. Menus do not use this; they stay at unity.
 * Re-exported so older imports keep a name for the duel bed.
 */
export { DUEL_ACTING_GAIN as MUSIC_LEVEL } from './levels';

export const DUEL_LOOP_SAMPLES = 7_938_000;
export const DUEL_RATE = 44_100;
/** 7,938,000 / 44,100. The published length is 180.0s. */
export const DUEL_LOOP_SECONDS = DUEL_LOOP_SAMPLES / DUEL_RATE;

export const LOBBY_OGG = '/audio/lobby-loop.ogg';
export const LOBBY_M4A = '/audio/lobby-loop.m4a';
/** Welcome to Thistlewood, used as the gameplay loop. */
export const DUEL_OGG = '/audio/title-loop.ogg';
export const DUEL_M4A = '/audio/title-loop.m4a';

const LOBBY_SCENES = new Set(['title', 'select', 'reveal', 'powerups', 'hub', 'road', 'streak', 'collection']);

export type MusicBed = 'lobby' | 'duel';

export function isLobbyScene(scene: string): boolean {
  return LOBBY_SCENES.has(scene);
}

/** Ogg is the exact sample loop. M4a is the fallback. */
export function lobbySources(support: { ogg: boolean; m4a: boolean }): string[] {
  return fileSources(LOBBY_OGG, LOBBY_M4A, support);
}

export function duelSources(support: { ogg: boolean; m4a: boolean }): string[] {
  return fileSources(DUEL_OGG, DUEL_M4A, support);
}

function fileSources(ogg: string, m4a: string, support: { ogg: boolean; m4a: boolean }): string[] {
  if (support.ogg && support.m4a) return [ogg, m4a];
  if (support.ogg) return [ogg];
  if (support.m4a) return [m4a];
  return [ogg, m4a];
}

/**
 * Sample-accurate loop end, in seconds, on this buffer's sample grid.
 * Loop start is always 0. AAC can run past the musical end, so the m4a loop
 * stops at the sample count instead of the padded file.
 */
export function sampleLoopEnd(
  samples: number,
  fileRate: number,
  duration: number,
  sampleRate: number,
): number {
  if (!(sampleRate > 0) || !(fileRate > 0)) return 0;
  const frame = Math.round((samples / fileRate) * sampleRate);
  const end = frame / sampleRate;
  if (!(duration > 0) || end > duration) return duration > 0 ? duration : end;
  return end;
}

export function lobbyLoopEnd(duration: number, sampleRate: number): number {
  return sampleLoopEnd(LOBBY_LOOP_SAMPLES, LOBBY_RATE, duration, sampleRate);
}

export function duelLoopEnd(duration: number, sampleRate: number): number {
  return sampleLoopEnd(DUEL_LOOP_SAMPLES, DUEL_RATE, duration, sampleRate);
}

export function lobbyLoopPoints(duration: number, sampleRate: number): { loopStart: number; loopEnd: number } {
  return { loopStart: 0, loopEnd: lobbyLoopEnd(duration, sampleRate) };
}

export function duelLoopPoints(duration: number, sampleRate: number): { loopStart: number; loopEnd: number } {
  return { loopStart: 0, loopEnd: duelLoopEnd(duration, sampleRate) };
}

export type LobbyAction =
  | { type: 'ensure'; bed: MusicBed }
  | { type: 'gain'; bed: MusicBed; value: number; seconds: number };

/**
 * Menus keep the lobby loop. A duel crossfades to the gameplay loop.
 * Moving between menus, or from the board to the results, does not restart either.
 */
export class LobbyDirector {
  private place: 'menu' | 'away' = 'away';
  private duelLive = false;

  enter(scene: string, muted: boolean): LobbyAction[] {
    if (isLobbyScene(scene)) {
      const staying = this.place === 'menu';
      const fromDuel = this.place === 'away' && this.duelLive;
      this.place = 'menu';
      const actions: LobbyAction[] = [{ type: 'ensure', bed: 'lobby' }];
      if (staying) return actions;
      const seconds = fromDuel && !muted ? LOBBY_FADE_SEC : 0;
      actions.push({ type: 'gain', bed: 'lobby', value: muted ? 0 : 1, seconds });
      if (fromDuel) actions.push({ type: 'gain', bed: 'duel', value: 0, seconds: muted ? 0 : LOBBY_FADE_SEC });
      return actions;
    }
    if (this.place === 'away' && this.duelLive) return [];
    const fromMenu = this.place === 'menu';
    this.place = 'away';
    this.duelLive = true;
    const seconds = muted ? 0 : LOBBY_FADE_SEC;
    const actions: LobbyAction[] = [
      { type: 'ensure', bed: 'duel' },
      { type: 'gain', bed: 'duel', value: muted ? 0 : 1, seconds },
    ];
    if (fromMenu) actions.push({ type: 'gain', bed: 'lobby', value: 0, seconds: LOBBY_FADE_SEC });
    return actions;
  }

  /** Mute snaps both beds. Unmute restores only the bed for the current place. */
  mute(muted: boolean): LobbyAction[] {
    if (muted || this.place !== 'menu') {
      return [
        { type: 'gain', bed: 'lobby', value: this.place === 'menu' && !muted ? 1 : 0, seconds: 0 },
        { type: 'gain', bed: 'duel', value: this.place === 'away' && !muted ? 1 : 0, seconds: 0 },
      ];
    }
    return [
      { type: 'gain', bed: 'lobby', value: 1, seconds: 0 },
      { type: 'gain', bed: 'duel', value: 0, seconds: 0 },
    ];
  }
}

interface Bed {
  buffer: AudioBuffer | null;
  loading: Promise<AudioBuffer | null> | null;
  source: AudioBufferSourceNode | null;
  gain: GainNode | null;
  booting: Promise<void> | null;
  target: number;
  fadeSec: number;
  wanted: boolean;
}

/**
 * One source per bed for the whole session. Scene changes only move the envelopes.
 * Both feed a quieter music bus, then the stinger duck, then the master volume.
 */
export class LobbyMusic {
  private readonly director = new LobbyDirector();
  private readonly beds: Record<MusicBed, Bed> = {
    lobby: emptyBed(),
    duel: emptyBed(),
  };
  /** Stinger duck. Victory and defeat fade this to silence. The power stinger leaves it open. */
  private duckGain: GainNode | null = null;
  /** Idle and the rival's turn. A duel holds this at the 12 dB duck. Menus leave it open. */
  private activityGain: GainNode | null = null;
  /** While true, acting cannot open the activity bus. */
  private duelDuckHeld = false;
  /** Voice lines. Independent of the activity duck so the two multiply. */
  private voiceDuckGain: GainNode | null = null;
  /** Trick casts. Match SFX are not on this node. */
  private castDuckGain: GainNode | null = null;
  private music: GainNode | null = null;
  private level = 1;
  private activity: DuelActivity = 'acting';
  /** Off mutes the duel bed only. The lobby loop is left alone. */
  private duelMusicOn = true;

  constructor(
    private readonly context: () => AudioContext | null,
    private readonly master: () => GainNode | null,
  ) {}

  /** Decode one bed. The title fetch is lobby only; the duel bed waits until the title is up. */
  preloadBed(bed: MusicBed): void {
    void this.loadBuffer(bed);
  }

  preload(): void {
    this.preloadBed('lobby');
    this.preloadBed('duel');
  }

  enter(scene: string, muted: boolean): void {
    this.apply(this.director.enter(scene, muted));
  }

  setMuted(muted: boolean): void {
    this.apply(this.director.mute(muted));
  }

  /** 0–1 slider. Does not restart the loops. The duel offset lives on the bed. */
  setLevel(amount: number): void {
    this.level = clamp01(amount);
    if (this.music) this.music.gain.value = this.level;
  }

  /** Duel music switch. Menus keep playing either way. */
  setDuelMusic(on: boolean): void {
    if (this.duelMusicOn === on) return;
    this.duelMusicOn = on;
    const state = this.beds.duel;
    if (state.fadeSec <= 0) state.fadeSec = 0.25;
    this.renderGain('duel');
  }

  /**
   * Hold the duel at the 12 dB duck. Passing false opens the bus again,
   * which is how a menu stops inheriting the duel level.
   */
  holdDuelDuck(held: boolean): void {
    this.duelDuckHeld = held;
    if (held) this.setActivity('idle');
    else this.setActivity('acting', 0.2);
  }

  /**
   * Acting opens the bed (800 ms) when the duel is not holding the duck.
   * Idle and the rival's turn drop it 12 dB over 300 ms. During a duel,
   * acting is ignored so the bed does not rise between turns.
   * `seconds` overrides the move.
   */
  setActivity(activity: DuelActivity, seconds?: number): void {
    const next = activityWhileDueling(this.duelDuckHeld, activity);
    if (this.duelDuckHeld && (this.activity === 'idle' || this.activity === 'rival')) {
      this.activity = 'idle';
      return;
    }
    this.activity = next;
    const node = this.activityBus();
    if (!node) return;
    const move = activityDuck(next);
    this.ramp(node, move.gain, seconds ?? move.seconds);
  }

  /** Duck under a voice line, then release across 400 ms from the end of the clip. */
  duckVoice(seconds: number): void {
    if (!voiceDucksMusic(this.duelDuckHeld)) return;
    const node = this.voiceDuckBus();
    if (!node) return;
    this.rampHold(node, VOICE_MUSIC_DUCK_GAIN, VOICE_DUCK_ATTACK_SEC, Math.max(0, seconds), VOICE_DUCK_RELEASE_SEC);
  }

  /** A line that stops early still releases over the voice release. */
  releaseVoice(): void {
    const node = this.voiceDuckGain;
    if (!node) return;
    this.ramp(node, 1, VOICE_DUCK_RELEASE_SEC);
  }

  /** Duck for a trick cast. Passing false releases the bed. */
  duckCast(down: boolean): void {
    const node = this.castDuckBus();
    if (!node) return;
    if (down) this.ramp(node, CAST_MUSIC_DUCK_GAIN, CAST_DUCK_ATTACK_SEC);
    else this.ramp(node, 1, CAST_DUCK_RELEASE_SEC);
  }

  /** Try again after a user gesture unlocks the context. Does not restart a live loop. */
  kick(): void {
    (['lobby', 'duel'] as const).forEach((bed) => {
      if (this.beds[bed].wanted && !this.beds[bed].source) void this.ensure(bed);
    });
  }

  /**
   * Fade the music bus out under a victory or defeat stinger, then open it
   * again. The bed envelopes are left alone. The power stinger does not call this.
   */
  duck(seconds: number): void {
    this.scheduleDuck(0, Math.min(STINGER_MUSIC_FADE, Math.max(0.05, seconds)), seconds);
  }

  /** Drop a stinger duck so mute, or the end of a clip, does not leave the music silent. */
  releaseDuck(): void {
    this.scheduleDuck(1, 0.12, 0);
  }

  private apply(actions: LobbyAction[]): void {
    for (const action of actions) {
      if (action.type === 'gain') this.fade(action.bed, action.value, action.seconds);
    }
    for (const action of actions) {
      if (action.type === 'ensure') void this.ensure(action.bed);
    }
  }

  private fade(bed: MusicBed, value: number, seconds: number): void {
    const state = this.beds[bed];
    state.target = value;
    state.fadeSec = seconds;
    this.renderGain(bed);
  }

  private ensure(bed: MusicBed): Promise<void> {
    const state = this.beds[bed];
    state.wanted = true;
    if (state.source) return Promise.resolve();
    if (state.booting) return state.booting;
    state.booting = this.boot(bed).finally(() => {
      state.booting = null;
    });
    return state.booting;
  }

  private async boot(bed: MusicBed): Promise<void> {
    const state = this.beds[bed];
    const ctx = this.context();
    if (!ctx || state.source) return;
    const buffer = await this.loadBuffer(bed);
    if (!buffer || state.source || ctx.state === 'suspended') return;
    const node = this.envelope(bed);
    if (!node) return;
    const src = ctx.createBufferSource();
    const points = bed === 'lobby'
      ? lobbyLoopPoints(buffer.duration, buffer.sampleRate)
      : duelLoopPoints(buffer.duration, buffer.sampleRate);
    src.buffer = buffer;
    src.loop = true;
    src.loopStart = points.loopStart;
    src.loopEnd = points.loopEnd;
    src.connect(node);
    try {
      src.start();
    } catch {
      return;
    }
    state.source = src;
    this.renderGain(bed);
  }

  private renderGain(bed: MusicBed): void {
    const ctx = this.context();
    const node = this.envelope(bed);
    if (!ctx || !node) return;
    const state = this.beds[bed];
    const now = ctx.currentTime;
    const param = node.gain;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    const target = this.audible(bed);
    if (state.fadeSec <= 0) param.setValueAtTime(target, now);
    else param.linearRampToValueAtTime(target, now + state.fadeSec);
  }

  /** Lobby stays at unity. The duel bed is -4 dB, or silent when duel music is off. */
  private audible(bed: MusicBed): number {
    const state = this.beds[bed];
    if (bed === 'lobby') return state.target * MENU_MUSIC_GAIN;
    if (!this.duelMusicOn) return 0;
    return state.target * DUEL_ACTING_GAIN;
  }

  private ramp(node: GainNode, target: number, seconds: number): void {
    const ctx = this.context();
    if (!ctx) return;
    const now = ctx.currentTime;
    const param = node.gain;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    if (seconds <= 0) param.setValueAtTime(target, now);
    else param.linearRampToValueAtTime(target, now + seconds);
  }

  private rampHold(node: GainNode, target: number, attack: number, hold: number, release: number): void {
    const ctx = this.context();
    if (!ctx) return;
    const now = ctx.currentTime;
    const param = node.gain;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    const down = now + Math.max(0.02, attack);
    const held = Math.max(down + 0.01, now + Math.max(0, hold));
    param.linearRampToValueAtTime(target, down);
    param.setValueAtTime(target, held);
    param.linearRampToValueAtTime(1, held + release);
  }

  private scheduleDuck(target: number, fade: number, hold: number): void {
    const ctx = this.context();
    const node = this.duckBus();
    if (!ctx || !node) return;
    const now = ctx.currentTime;
    const param = node.gain;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    const reach = now + Math.max(0.02, fade);
    param.linearRampToValueAtTime(target, reach);
    if (target === 0 && hold > fade) {
      const back = now + hold;
      param.setValueAtTime(0, Math.max(reach, back - 0.05));
      param.linearRampToValueAtTime(1, back + LOBBY_FADE_SEC);
    }
  }

  private envelope(bed: MusicBed): GainNode | null {
    const ctx = this.context();
    const music = this.musicBus();
    if (!ctx || !music) return null;
    const state = this.beds[bed];
    if (!state.gain) {
      state.gain = ctx.createGain();
      state.gain.gain.value = 0;
      state.gain.connect(music);
    }
    return state.gain;
  }

  /** Slider only. Category offsets and ducks sit on the nodes around it. */
  private musicBus(): GainNode | null {
    const ctx = this.context();
    const next = this.activityBus();
    if (!ctx || !next) return null;
    if (!this.music) {
      this.music = ctx.createGain();
      this.music.gain.value = this.level;
      this.music.connect(next);
    }
    return this.music;
  }

  private activityBus(): GainNode | null {
    return this.sideBus('activity', () => activityDuck(this.activity).gain, () => this.voiceDuckBus());
  }

  private voiceDuckBus(): GainNode | null {
    return this.sideBus('voice', () => 1, () => this.castDuckBus());
  }

  private castDuckBus(): GainNode | null {
    return this.sideBus('cast', () => 1, () => this.duckBus());
  }

  private sideBus(
    which: 'activity' | 'voice' | 'cast',
    initial: () => number,
    next: () => GainNode | null,
  ): GainNode | null {
    const ctx = this.context();
    const dest = next();
    if (!ctx || !dest) return null;
    const current = which === 'activity' ? this.activityGain : which === 'voice' ? this.voiceDuckGain : this.castDuckGain;
    if (current) return current;
    const node = ctx.createGain();
    node.gain.value = initial();
    node.connect(dest);
    if (which === 'activity') this.activityGain = node;
    else if (which === 'voice') this.voiceDuckGain = node;
    else this.castDuckGain = node;
    return node;
  }

  /** Sits under the cast duck and above the master volume. */
  private duckBus(): GainNode | null {
    const ctx = this.context();
    const master = this.master();
    if (!ctx || !master) return null;
    if (!this.duckGain) {
      this.duckGain = ctx.createGain();
      this.duckGain.gain.value = 1;
      this.duckGain.connect(master);
    }
    return this.duckGain;
  }

  private loadBuffer(bed: MusicBed): Promise<AudioBuffer | null> {
    const state = this.beds[bed];
    if (state.buffer) return Promise.resolve(state.buffer);
    if (state.loading) return state.loading;
    const job = this.decode(bed).then((buffer) => {
      if (buffer) state.buffer = buffer;
      if (state.loading === job) state.loading = null;
      return buffer;
    });
    state.loading = job;
    return job;
  }

  private async decode(bed: MusicBed): Promise<AudioBuffer | null> {
    const ctx = this.context();
    if (!ctx || typeof fetch !== 'function') return null;
    const support = this.support();
    const urls = bed === 'lobby' ? lobbySources(support) : duelSources(support);
    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const data = await response.arrayBuffer();
        return await ctx.decodeAudioData(data.slice(0));
      } catch {
        // The other file may decode.
      }
    }
    return null;
  }

  private support(): { ogg: boolean; m4a: boolean } {
    if (typeof document === 'undefined') return { ogg: true, m4a: true };
    const audio = document.createElement('audio');
    const yes = (mime: string) => {
      const value = audio.canPlayType(mime);
      return value === 'probably' || value === 'maybe';
    };
    return {
      ogg: yes('audio/ogg; codecs="vorbis"'),
      m4a: yes('audio/mp4; codecs="mp4a.40.2"'),
    };
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(1, Math.max(0, value));
}

function emptyBed(): Bed {
  return {
    buffer: null,
    loading: null,
    source: null,
    gain: null,
    booting: null,
    target: 0,
    fadeSec: 0,
    wanted: false,
  };
}
